import type { Provider, ProviderField } from '../types';

export const PROVIDERS: Provider[] = [
	{
		key: 'sendgrid',
		name: 'SendGrid',
		logo: 'SG',
		type: 'Email',
		desc: 'Reliable transactional email with ECDSA webhook delivery tracking.',
	},
	{
		key: 'ses',
		name: 'AWS SES',
		logo: 'SES',
		type: 'Email',
		desc: 'High-volume cloud email via AWS. SigV4 auth.',
	},
	{
		key: 'mailgun',
		name: 'Mailgun',
		logo: 'MG',
		type: 'Email',
		desc: 'Developer-focused email API. US and EU regions.',
	},
	{
		key: 'mailchimp',
		name: 'Mailchimp/Mandrill',
		logo: 'MC',
		type: 'Email',
		desc: 'Marketing campaigns + transactional via Mandrill.',
	},
	{
		key: 'pepipost',
		name: 'Pepipost/Netcore',
		logo: 'PP',
		type: 'Email',
		desc: 'India-optimised. Jio/Airtel/BSNL whitelisted IPs.',
		disabled: true,
	},
	{
		key: 'fcm',
		name: 'FCM',
		logo: 'FCM',
		type: 'Push',
		desc: 'Firebase Cloud Messaging. HTTP v1 only.',
	},
	{
		key: 'apns',
		name: 'APNs',
		logo: 'APNs',
		type: 'Push',
		desc: 'Apple Push. .p8 token auth over HTTP/2.',
	},
	{
		key: 'onesignal',
		name: 'OneSignal',
		logo: '1S',
		type: 'Push',
		desc: 'Multi-platform push. Segments and A/B testing.',
	},
	{
		key: 'clevertap',
		name: 'CleverTap',
		logo: 'CT',
		type: 'Push',
		desc: 'India/APAC engagement. Region-specific endpoints.',
	},
	{
		key: 'moengage',
		name: 'MoEngage',
		logo: 'MoE',
		type: 'Push',
		desc: 'India/SEA push. Multi-datacenter.',
	},
	{
		key: 'twilio',
		name: 'Twilio',
		logo: 'TW',
		type: 'Sms',
		desc: 'Global SMS. Delivery webhooks and callbacks.',
	},
	{
		key: 'infobip',
		name: 'Infobip',
		logo: 'IB',
		type: 'Sms',
		desc: 'Enterprise SMS. 190+ countries.',
	},
	{
		key: 'semaphore',
		name: 'Semaphore',
		logo: 'SM',
		type: 'Sms',
		desc: 'Philippines SMS. Globe and Smart networks.',
	},
	{
		key: 'smart',
		name: 'Smart Comms',
		logo: 'SC',
		type: 'Sms',
		desc: 'Smart PH carrier direct gateway.',
	},
	{
		key: 'globe',
		name: 'Globe Telecom',
		logo: 'GT',
		type: 'Sms',
		desc: 'Globe PH carrier. Short codes.',
	},
];

export const PROVIDER_FIELDS: Record<string, ProviderField[]> = {
	sendgrid: [
		{ label: 'Label', required: true, placeholder: 'e.g. Transactional' },
		{
			label: 'API Key',
			required: true,
			placeholder: 'SG.xxxxxxxxxxxxxxxx',
			password: true,
		},
		{
			label: 'From Email',
			required: true,
			placeholder: 'noreply@yourdomain.com',
		},
		{ label: 'From Name', required: true, placeholder: 'Fyscal Technologies' },
		{
			label: 'Webhook Signing Key',
			required: false,
			placeholder: 'MFkwEwYH...',
			password: true,
			hint: 'Found in Mail Settings → Event Webhook',
		},
		{
			label: 'Sandbox Mode',
			required: false,
			selectOptions: ['Disabled', 'Enabled'],
		},
	],
	ses: [
		{ label: 'Label', required: true, placeholder: 'e.g. Emergency Email' },
		{
			label: 'AWS Access Key ID',
			required: true,
			placeholder: 'AKIAIOSFODNN7EXAMPLE',
			password: true,
		},
		{
			label: 'AWS Secret Access Key',
			required: true,
			placeholder: 'wJalrXUtnFEMI/...',
			password: true,
		},
		{
			label: 'Region',
			required: true,
			selectOptions: [
				'ap-south-1',
				'us-east-1',
				'us-west-2',
				'eu-west-1',
				'ap-southeast-1',
			],
			hint: 'Must match verified identity region',
		},
		{
			label: 'From Email',
			required: true,
			placeholder: 'noreply@yourdomain.com',
		},
		{ label: 'From Name', required: true, placeholder: 'Fyscal Technologies' },
		{
			label: 'SNS Topic ARN',
			required: false,
			placeholder: 'arn:aws:sns:ap-south-1:123456:ses-events',
		},
	],
	mailgun: [
		{ label: 'Label', required: true, placeholder: 'e.g. Backup Email' },
		{
			label: 'Private API Key',
			required: true,
			placeholder: 'key-xxxxxxxx',
			password: true,
		},
		{
			label: 'Sending Domain',
			required: true,
			placeholder: 'mg.yourdomain.com',
		},
		{
			label: 'Region',
			required: true,
			selectOptions: ['US (api.mailgun.net)', 'EU (api.eu.mailgun.net)'],
			hint: 'Wrong region = 401 errors',
		},
		{
			label: 'From Email',
			required: true,
			placeholder: 'noreply@mg.yourdomain.com',
		},
		{ label: 'From Name', required: true, placeholder: 'Fyscal Technologies' },
		{
			label: 'Webhook Signing Key',
			required: false,
			placeholder: 'xxxxxxxx',
			password: true,
		},
	],
	mailchimp: [
		{ label: 'Label', required: true, placeholder: 'e.g. Marketing Email' },
		{
			label: 'Mailchimp API Key',
			required: true,
			placeholder: 'xxxxxxxx-us14',
			password: true,
			hint: 'Suffix e.g. -us14 is your datacenter',
		},
		{
			label: 'Mandrill API Key',
			required: false,
			placeholder: 'xxxxxxxx',
			password: true,
			hint: 'Mailchimp → Transactional → Settings. Paid add-on required.',
		},
		{ label: 'Audience List ID', required: true, placeholder: 'abc123def4' },
		{
			label: 'From Email',
			required: true,
			placeholder: 'noreply@yourdomain.com',
		},
		{ label: 'From Name', required: true, placeholder: 'Fyscal Technologies' },
	],
	fcm: [
		{ label: 'Label', required: true, placeholder: 'e.g. Android Push' },
		{
			label: 'Service Account JSON',
			required: true,
			placeholder: 'Paste full service account JSON here...',
			textarea: true,
			hint: 'Firebase Console → Project Settings → Service Accounts',
		},
		{
			label: 'Firebase Project ID',
			required: true,
			placeholder: 'my-project-12345',
			hint: 'Auto-extractable from service account JSON',
		},
		{
			label: 'VAPID Public Key',
			required: false,
			placeholder: 'BEl62iU...',
			hint: 'Required for web push only',
		},
	],
	apns: [
		{ label: 'Label', required: true, placeholder: 'e.g. iOS Push' },
		{
			label: '.p8 Private Key',
			required: true,
			placeholder: 'Paste AuthKey_XXXXXXXXXX.p8 contents...',
			textarea: true,
			hint: 'Apple Developer → Keys. Shown only once.',
		},
		{ label: 'Key ID', required: true, placeholder: 'ABC123DEF4' },
		{ label: 'Team ID', required: true, placeholder: 'XYZ987WVU6' },
		{ label: 'Bundle ID', required: true, placeholder: 'com.company.appname' },
		{
			label: 'APNs Environment',
			required: true,
			selectOptions: ['Production', 'Sandbox'],
		},
	],
	onesignal: [
		{ label: 'Label', required: true, placeholder: 'e.g. Cross-platform Push' },
		{ label: 'App ID', required: true, placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx' },
		{
			label: 'REST API Key',
			required: true,
			placeholder: 'xxxxxxxx',
			password: true,
		},
	],
	clevertap: [
		{ label: 'Label', required: true, placeholder: 'e.g. CleverTap Push' },
		{ label: 'Account ID', required: true, placeholder: 'XXX-XXX-XXXXXX' },
		{
			label: 'Account Passcode',
			required: true,
			placeholder: 'xxxxxxxx',
			password: true,
		},
		{
			label: 'Region',
			required: true,
			selectOptions: [
				'India (in1)',
				'US (us1)',
				'Singapore (sg1)',
				'Europe (eu1)',
			],
		},
	],
	moengage: [
		{ label: 'Label', required: true, placeholder: 'e.g. MoEngage Push' },
		{
			label: 'App ID',
			required: true,
			placeholder: 'XXXXXXXXXXXXXXXXXXXXXXXX',
		},
		{
			label: 'Data API Key',
			required: true,
			placeholder: 'xxxxxxxx',
			password: true,
		},
		{
			label: 'Data Center',
			required: true,
			selectOptions: [
				'India (DC-3)',
				'US (DC-1)',
				'EU (DC-2)',
				'Singapore (DC-4)',
				'Indonesia (DC-5)',
			],
		},
	],
	twilio: [
		{ label: 'Label', required: true, placeholder: 'e.g. Primary SMS' },
		{
			label: 'Account SID',
			required: true,
			placeholder: 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
		},
		{
			label: 'Auth Token',
			required: true,
			placeholder: 'xxxxxxxx',
			password: true,
		},
		{
			label: 'From Number / Sender ID',
			required: true,
			placeholder: '+1234567890',
		},
		{
			label: 'Status Callback URL',
			required: false,
			placeholder: 'https://yourplatform.com/webhooks/twilio',
		},
	],
	infobip: [
		{ label: 'Label', required: true, placeholder: 'e.g. SMS Fallback' },
		{
			label: 'Base URL',
			required: true,
			placeholder: 'xxxxx.api.infobip.com',
			hint: 'Unique to your account — found in Infobip dashboard',
		},
		{
			label: 'API Key',
			required: true,
			placeholder: 'xxxxxxxx',
			password: true,
		},
		{ label: 'Sender ID', required: true, placeholder: 'FyscalTech' },
	],
	semaphore: [
		{ label: 'Label', required: true, placeholder: 'e.g. PH SMS' },
		{
			label: 'API Key',
			required: true,
			placeholder: 'xxxxxxxx',
			password: true,
		},
		{
			label: 'Sender Name',
			required: true,
			placeholder: 'FYSCAL',
			hint: 'Must be pre-registered. Max 11 characters.',
		},
	],
	smart: [
		{ label: 'Label', required: true, placeholder: 'e.g. Smart PH SMS' },
		{ label: 'Client ID', required: true, placeholder: 'xxxxxxxx' },
		{
			label: 'Client Secret',
			required: true,
			placeholder: 'xxxxxxxx',
			password: true,
		},
		{ label: 'Short Code / Sender', required: true, placeholder: 'FYSCAL' },
	],
	globe: [
		{ label: 'Label', required: true, placeholder: 'e.g. Globe PH SMS' },
		{ label: 'App ID', required: true, placeholder: 'xxxxxxxx' },
		{
			label: 'App Secret',
			required: true,
			placeholder: 'xxxxxxxx',
			password: true,
		},
		{ label: 'Short Code', required: true, placeholder: '21581234' },
	],
};

export function getProviderFields(key: string): ProviderField[] {
	return PROVIDER_FIELDS[key] ?? PROVIDER_FIELDS.sendgrid;
}
